import { InfoCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, Modal } from 'antd';
import { useHistory } from 'react-router-dom';

import { useDuplicateHotel } from 'app/hooks/data/hotels/useHotel';
import type { DuplicateHotelPayload } from 'app/hooks/data/hotels/useHotel';

import './DuplicateModal.scss';

type DuplicateModalProps = {
  open: boolean;
  onClose: () => void;
  hotelId: string;
};

export const DuplicateModal = ({
  open,
  onClose,
  hotelId,
}: DuplicateModalProps) => {
  const history = useHistory();
  const [form] = Form.useForm();
  const { mutateAsync: duplicateHotel, isLoading } = useDuplicateHotel(hotelId);
  const queryClient = useQueryClient();

  const handleOk = async (values: DuplicateHotelPayload) => {
    const { hotelId: newHotelId } = await duplicateHotel(values);

    queryClient.invalidateQueries();

    history.push(`/hotels/${newHotelId}/edit`);
    onClose();
  };

  return (
    <Modal
      title="Duplicate hotel"
      open={open}
      width={572}
      closable
      destroyOnClose
      onCancel={onClose}
      centered
      className="duplicate-hotel-modal"
      footer={[
        <Button onClick={onClose}>No</Button>,
        <Button onClick={form.submit} type="primary" loading={isLoading}>
          Yes
        </Button>,
      ]}
    >
      <Form onFinish={handleOk} form={form} layout="vertical">
        <div className="form-field">
          <div className="title">Hotel name</div>
          <Form.Item
            label="Hotel name"
            name="name"
            rules={[{ required: true, message: 'New hotel name is required' }]}
            noStyle
          >
            <Input type="text" placeholder="Duplicate hotel name" />
          </Form.Item>
        </div>
        <div className="description">
          <InfoCircleOutlined />
          Are you sure you want to duplicate this hotel ? (This action is
          irrevocable)
        </div>
      </Form>
    </Modal>
  );
};
